// SalesForecastModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Flex,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Button,
  Text,
  Box,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  Badge,
  useColorModeValue,
  HStack,
} from "@chakra-ui/react";
import axios from "../../api/index";

// نگاشت گروه وضعیت 1..10 به برچسب‌های ظرفیت سفارشی
const capacityLabelMap = [
  "Z1 PS4", // 1
  "Z1 PS4", // 2
  "Z2 PS4", // 3
  "Z3 PS4", // 4
  "Z3 PS4", // 5
  "Z1 PS5", // 6
  "Z1 PS5", // 7
  "Z2 PS5", // 8
  "Z3 PS5", // 9
  "Z3 PS5", // 10
];
const getCapacityLabel = (groupStatus) => {
  const idx = Number(groupStatus) - 1;
  if (idx >= 0 && idx < capacityLabelMap.length) return capacityLabelMap[idx];
  return `Z${groupStatus}`;
};

const SalesForecastModal = ({
  isOpen,
  onClose,
  productId,
  productName,
  defaultForecastDays = 60,
  defaultHistoryDays = 360,
}) => {
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState("");
  const [forecastDays, setForecastDays] = useState(defaultForecastDays);
  const [historyDays, setHistoryDays] = useState(defaultHistoryDays);
  const [ps4Rows, setPs4Rows] = useState([]); // [{group_status, forecast_needed}]
  const [ps5Rows, setPs5Rows] = useState([]); // [{group_status, forecast_needed}]

  const panelBg = useColorModeValue("white", "gray.800");
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.300");

  const resetState = () => {
    setForecastError("");
    setPs4Rows([]);
    setPs5Rows([]);
  };

  const extractRows = (data, platformKey) => {
    const caps = data?.platforms?.[platformKey]?.capacities || [];
    const rows = caps.map((c) => ({
      group_status: Number(c.group_status ?? 0),
      forecast_needed: Number(c.forecast_needed ?? 0),
    }));
    rows.sort((a, b) => a.group_status - b.group_status);
    return rows;
  };

  const fetchForecast = async (pid, fDays, hDays) => {
    if (!pid) return;
    setForecastLoading(true);
    setForecastError("");
    setPs4Rows([]);
    setPs5Rows([]);

    try {
      const res = await axios.get(`/reports/sales-forecast`, {
        params: { product_id: pid, forecast_days: fDays, history_days: hDays },
      });

      const data = res?.data?.data?.[0] ?? null;
      if (!data) {
        resetState();
        setForecastError("داده‌ای برای نمایش دریافت نشد.");
        return;
      }

      setPs4Rows(extractRows(data, "ps4"));
      setPs5Rows(extractRows(data, "ps5"));
    } catch (e) {
      setForecastError("خطا در دریافت پیش‌بینی");
    } finally {
      setForecastLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && productId) {
      setForecastDays(defaultForecastDays);
      setHistoryDays(defaultHistoryDays);
      fetchForecast(productId, defaultForecastDays, defaultHistoryDays);
    } else if (!isOpen) {
      resetState();
    }
  }, [isOpen, productId, defaultForecastDays, defaultHistoryDays]);

  const z2ps5Need = useMemo(() => (ps5Rows.find(r => getCapacityLabel(r.group_status) === 'Z2 PS5')?.forecast_needed) || 0, [ps5Rows]);

  const PlatformTable = ({ title, rows, color, highlightLabel }) => (
    <Box
      borderWidth="1px"
      borderRadius="xl"
      p={4}
      bg={panelBg}
      borderColor={panelBorder}
      boxShadow={useColorModeValue("sm", "sm")}
      flex="1"
    >
      <Text fontWeight="bold" mb={2}>{title}</Text>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>ظرفیت</Th>
            <Th textAlign="center">تعداد موردنیاز</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((r, idx) => {
            const label = getCapacityLabel(r.group_status);
            const isHighlight = label === highlightLabel;
            return (
              <Tr key={`${title}-${idx}`} bg={isHighlight ? useColorModeValue("yellow.50", "whiteAlpha.100") : undefined}>
                <Td>
                  <Badge variant={isHighlight ? "solid" : "subtle"} colorScheme={isHighlight ? "yellow" : color}>{label}</Badge>
                </Td>
                <Td textAlign="center">
                  <Badge colorScheme={r.forecast_needed > 0 ? (isHighlight ? "yellow" : "green") : "gray"}>
                    {r.forecast_needed}
                  </Badge>
                </Td>
              </Tr>
            );
          })}
          {!rows.length && (
            <Tr>
              <Td colSpan={2}>
                <Text fontSize="sm" color="gray.500">داده‌ای ندارد</Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} size="5xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton left="8px" right="unset" />
        <ModalBody p={4}>
          <Flex align="center" justify="space-between" mb={3}>
            <HStack spacing={3}>
              <Text fontWeight="bold" fontSize="lg">پیش‌بینی فروش</Text>
              {productName && <Badge colorScheme="blue" fontSize="md" borderRadius="md">{productName}</Badge>}
              <Badge colorScheme="yellow" fontSize="md" borderRadius="md">Z2 PS5</Badge>
              <Badge colorScheme="purple" fontSize="md" borderRadius="md">تعداد: {z2ps5Need}</Badge>
            </HStack>
          </Flex>

          <Flex gap={3} mb={4} wrap="wrap" align="center">
            <FormControl w={{ base: "100%", sm: "180px" }}>
              <FormLabel fontSize="sm">روزهای پیش‌بینی</FormLabel>
              <NumberInput
                min={1}
                value={forecastDays}
                onChange={(_, v) => setForecastDays(Number.isFinite(v) ? v : 0)}
              >
                <NumberInputField />
              </NumberInput>
            </FormControl>

            <FormControl w={{ base: "100%", sm: "180px" }}>
              <FormLabel fontSize="sm">روزهای تاریخچه</FormLabel>
              <NumberInput
                min={1}
                value={historyDays}
                onChange={(_, v) => setHistoryDays(Number.isFinite(v) ? v : 0)}
              >
                <NumberInputField />
              </NumberInput>
            </FormControl>

            <Button
              colorScheme="teal"
              onClick={() => fetchForecast(productId, forecastDays, historyDays)}
              isLoading={forecastLoading}
              loadingText="به‌روزرسانی…"
            >
              به‌روزرسانی
            </Button>
          </Flex>

          {forecastLoading && (
            <Flex align="center" gap={2}>
              <Spinner size="sm" />
              <Text>در حال بارگذاری…</Text>
            </Flex>
          )}

          {forecastError && !forecastLoading && (
            <Text color="red.500">{forecastError}</Text>
          )}

          {!forecastLoading && !forecastError && (
            <>
              <Flex gap={4} direction={{ base: "column", md: "row" }}>
                <PlatformTable title="PS4" rows={ps4Rows} color="purple" />
                <PlatformTable title="PS5" rows={ps5Rows} color="pink" highlightLabel="Z2 PS5" />
              </Flex>

              {!(ps4Rows.length || ps5Rows.length) && (
                <>
                  <Divider my={3} />
                  <Text>داده‌ای برای نمایش وجود ندارد.</Text>
                </>
              )}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SalesForecastModal;
